import { fetchConfig } from '@theme/utilities';

const POLICY_ATTRIBUTE_KEYS = [
  '__Policy acceptance',
  '__Policy accepted at UTC',
  '__Policy consent version',
  '__Policy consent source',
  '__Policy consent language',
  '__Policy URLs',
];

const policyConsentState = {
  initialized: false,
  accepted: false,
  saved: false,
  saving: false,
  error: '',
  revision: 0,
  /** @type {Promise<void> | null} */
  savePromise: null,
};

let cartAttributeWrite = Promise.resolve();

class PolicyConsent extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.addEventListener('change', this.#handleChange, { signal: this.#abortController.signal });
    this.addEventListener('click', this.#handleClick, {
      capture: true,
      signal: this.#abortController.signal,
    });
    document.addEventListener('policy-consent:update', this.#syncView, {
      signal: this.#abortController.signal,
    });

    if (!policyConsentState.initialized) {
      policyConsentState.initialized = true;
      policyConsentState.revision += 1;
      this.#clearAcceptance();
    }

    this.#syncView();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-policy-consent-checkbox]')) {
      return;
    }

    policyConsentState.revision += 1;
    policyConsentState.accepted = event.target.checked;
    policyConsentState.saved = false;
    policyConsentState.error = '';

    if (event.target.checked) {
      this.#saveAcceptance(policyConsentState.revision);
    } else {
      this.#clearAcceptance();
    }

    this.#notifyUpdate();
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const expressGuard = event.target.closest('[data-policy-consent-guard]');
    if (expressGuard) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#showRequirement();
      return;
    }

    const checkoutButton = event.target.closest('[data-policy-consent-checkout]');
    if (!(checkoutButton instanceof HTMLButtonElement)) return;

    if (!policyConsentState.accepted) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#showRequirement();
      return;
    }

    if (!policyConsentState.saved) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#finishCheckout(checkoutButton);
    }
  };

  /**
   * @param {number} revision
   */
  #saveAcceptance(revision) {
    const acceptedAt = new Date().toISOString();
    const termsUrl = this.#absoluteUrl(this.dataset.termsUrl);
    const refundUrl = this.#absoluteUrl(this.dataset.refundUrl);
    const attributes = {
      '__Policy acceptance': 'I have read and agree to the Terms of Service and Return & Refund Policy.',
      '__Policy accepted at UTC': acceptedAt,
      '__Policy consent version': this.dataset.consentVersion || 'cart-v1',
      '__Policy consent source': this.dataset.consentSource || 'cart',
      '__Policy consent language': document.documentElement.lang || 'en',
      '__Policy URLs': `${termsUrl} | ${refundUrl}`,
    };

    policyConsentState.saving = true;
    policyConsentState.savePromise = this.#updateCartAttributes(attributes)
      .then(() => {
        if (revision !== policyConsentState.revision || !policyConsentState.accepted) return;

        policyConsentState.saved = true;
        policyConsentState.error = '';
      })
      .catch(() => {
        if (revision !== policyConsentState.revision) return;

        policyConsentState.accepted = false;
        policyConsentState.saved = false;
        policyConsentState.error = "We couldn't save your selection. Please try again.";
      })
      .finally(() => {
        if (revision === policyConsentState.revision) {
          policyConsentState.saving = false;
          this.#notifyUpdate();
        }
      });
  }

  #clearAcceptance() {
    const revision = policyConsentState.revision;
    const emptyAttributes = Object.fromEntries(POLICY_ATTRIBUTE_KEYS.map((key) => [key, '']));

    policyConsentState.saving = true;
    policyConsentState.savePromise = this.#updateCartAttributes(emptyAttributes)
      .catch(() => {})
      .finally(() => {
        if (revision === policyConsentState.revision) {
          policyConsentState.saving = false;
          this.#notifyUpdate();
        }
      });
  }

  /**
   * @param {HTMLButtonElement} checkoutButton
   */
  async #finishCheckout(checkoutButton) {
    policyConsentState.error = '';
    this.#notifyUpdate();

    try {
      await policyConsentState.savePromise;
    } catch (error) {
      return;
    }

    if (!policyConsentState.accepted || !policyConsentState.saved) {
      if (policyConsentState.error) {
        this.#focusCheckbox();
      } else {
        this.#showRequirement();
      }
      return;
    }

    if (checkoutButton.form) {
      checkoutButton.form.requestSubmit(checkoutButton);
      return;
    }

    window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
  }

  /**
   * @param {Record<string, string>} attributes
   */
  async #updateCartAttributes(attributes) {
    const request = cartAttributeWrite.catch(() => {}).then(async () => {
      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body: JSON.stringify({ attributes }) }),
        keepalive: true,
      });

      if (!response.ok) throw new Error('Unable to save policy consent');
    });

    cartAttributeWrite = request;
    return request;
  }

  #showRequirement() {
    policyConsentState.error = policyConsentState.accepted && policyConsentState.saving
      ? 'Saving your selection. Please wait.'
      : 'Please select the checkbox to continue.';
    this.#notifyUpdate();

    this.#focusCheckbox();
  }

  #focusCheckbox() {
    const checkbox = this.querySelector('[data-policy-consent-checkbox]');
    if (checkbox instanceof HTMLInputElement) checkbox.focus({ preventScroll: true });
  }

  #syncView = () => {
    const checkbox = this.querySelector('[data-policy-consent-checkbox]');
    const error = this.querySelector('[data-policy-consent-error]');
    const checkoutButton = this.querySelector('[data-policy-consent-checkout]');
    const expressArea = this.querySelector('[data-policy-consent-express]');
    const expressGuard = this.querySelector('[data-policy-consent-guard]');

    if (checkbox instanceof HTMLInputElement) {
      checkbox.checked = policyConsentState.accepted;
      checkbox.setAttribute('aria-invalid', policyConsentState.error ? 'true' : 'false');
    }

    if (error instanceof HTMLElement) {
      error.textContent = policyConsentState.error;
      error.hidden = !policyConsentState.error;
    }

    if (checkoutButton instanceof HTMLButtonElement) {
      checkoutButton.disabled = false;
      checkoutButton.setAttribute('aria-busy', policyConsentState.saving ? 'true' : 'false');
    }

    if (expressArea instanceof HTMLElement) {
      expressArea.inert = !policyConsentState.saved;
      expressArea.setAttribute('aria-disabled', policyConsentState.saved ? 'false' : 'true');
    }

    if (expressGuard instanceof HTMLButtonElement) {
      expressGuard.hidden = policyConsentState.saved;
    }
  };

  #notifyUpdate() {
    document.dispatchEvent(new CustomEvent('policy-consent:update'));
  }

  /**
   * @param {string | undefined} value
   */
  #absoluteUrl(value) {
    return new URL(value || '/', window.location.origin).href;
  }
}

if (!customElements.get('policy-consent')) {
  customElements.define('policy-consent', PolicyConsent);
}
